import React, { useEffect, useState } from 'react';
import SecondaryButton from 'components/buttons/SecondaryButton';
import { useGlobalContext } from 'GlobalContext';
import { useTranslation } from 'react-i18next';
import { saveData } from 'services/api/saveData';
import { useFetchData } from 'services/api/useFetchData';

interface IntegrationsNotificationsListProps {
    integrationSlug: string;
    integrationId?: number;
}

const IntegrationNotificationsList: React.FC<IntegrationsNotificationsListProps> = ({ 
    integrationSlug, 
    integrationId
}) => {
    const { t } = useTranslation();
    const { setFloatingAlert } = useGlobalContext();
    const { response: integrationNotifications, refetch: refetchNotifications } = useFetchData({apiUrl: integrationId ? `get_integration_notification_list/${integrationSlug}/${integrationId}` : ''});
    const [resolvingIds, setResolvingIds] = useState<number[]>([]);

    // Poll for new notifications, so ones created while this page is
    // already open (e.g. by webhook processing during an in-progress
    // import) show up without a manual reload
    useEffect(() => {
        if (!integrationId) return;
        const interval = setInterval(() => {
            refetchNotifications();
        }, 5000);
        return () => clearInterval(interval);
    }, [integrationId, refetchNotifications]);

    // Handle resolving of an integration notification
    const handleResolveNotification = async (notificationId: number) => {

        // Set loading to true for the specific notification
        setResolvingIds(prev => [...prev, notificationId]);

        try {
            // Save in the backend
            const response = await saveData({
                apiUrl: `resolve_integration_notification/${notificationId}`,
                method: 'post',
                data: {}
            });

            // Refetch the notification list
            if (response?.status === 200) {
                refetchNotifications();
            }
        } catch (error) {
            console.error('Error resolving notification', error);
        } finally {
            // Stop loading
            setResolvingIds(prev => prev.filter(id => id !== notificationId));

            // Show floating alert
            setFloatingAlert({
                type: 'success',
                message: 'integrations.general.integration_notification_resolved'
            });
        }
    };

    return (
        integrationNotifications?.results?.length > 0 ? (
            <div className="integration-notifications">
                <div className='notifications-header'>
                    <h3>
                        {t('integrations.general.integration_notification_label')}
                    </h3>
                    <p>
                        {t('integrations.general.integration_notification_description')} 
                    </p>
                </div>
                <div className="notification-list">
                    {integrationNotifications.results.map((notification: any) => {

                        // Get the notification context parts
                        const contextParts = [
                            notification.entity
                                ? t(`integrations.general.${notification.integration_name}.${notification.entity}`)
                                : null,

                            notification.external_id
                                ? t('integrations.general.external_id', { id: notification.external_id })
                                : null,

                            notification.object_model && notification.object_id
                                ? t(`integrations.general.${notification.integration_name}.${notification.object_model}`
                                ) + ` #${notification.object_id}`
                                : null,

                            notification.created
                                ? new Date(notification.created).toLocaleString()
                                : null,
                        ].filter(Boolean);

                        return (
                            <div
                                key={notification.id}
                                className='card notification-item'
                            >
                                <div>
                                    {contextParts.length > 0 && (
                                        <div className="notification-context">
                                            {contextParts.join(' • ')}
                                            <span> • </span>
                                            <span className={`notification-status-badge ${notification.severity}`}>
                                                {t(`integrations.notifications.severity_choices.${notification.severity}`)}
                                            </span>
                                        </div>
                                    )}

                                    <div className="notification-title">
                                        {t(`integrations.notifications.${notification.integration_name}.${notification.title}`)}
                                    </div>

                                    {notification.message && (
                                        <div className="notification-message">
                                            {notification.message}
                                        </div>
                                    )}
                                </div>

                                <SecondaryButton
                                    label={t('integrations.general.integration_notification_resolve')} 
                                    size="small"
                                    loading={resolvingIds.includes(notification.id)}
                                    onClick={() => handleResolveNotification(notification.id)}
                                />
                            </div>
                        )
                    })}
                </div>
            </div>
        ) : null
    );
};

export default IntegrationNotificationsList;